import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePagination from "../hooks/pagination";
import preIcon from "../assets/img/previous.svg";
import nextIcon from "../assets/img/next.svg";
import { useTranslation } from "react-i18next";

const ProductSelection = ({
  products,
  handleSelectedProducts,
  handleIsOpen,
  isPitara,
  isServices,
  isRawMaterials = false,
  isPurchase = false,
}) => {
  const navigate = useNavigate();
  const { bussinessUuid } = useParams();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [taxIncludedMap, setTaxIncludedMap] = useState({}); // Track tax included for each product
  const pageLimit = 12;
  const { t } = useTranslation();

  const { pageNumber, pageCount, pageData, nextPage, previousPage } =
    usePagination(products, pageLimit);

  useEffect(() => {
    const updatedProducts = products.map((product) => {
      return {
        ...product,
        checked: false,
        // Initialize taxIncluded from product data or default to false
        taxIncluded: product.taxIncluded || false,
      };
    });
    setSelectedProducts(updatedProducts);

    // Initialize taxIncludedMap with existing product taxIncluded values
    const initialTaxMap = {};
    products.forEach((product) => {
      const productId = getProductId(product);
      initialTaxMap[productId] = product.taxIncluded || false;
    });
    setTaxIncludedMap(initialTaxMap);
  }, [products]);

  // Helper function to get product ID based on type
  const getProductId = (product) => {
    if (isPitara === true) {
      return product._id;
    } else if (isServices === true) {
      return product.serviceId;
    } else if (isRawMaterials === true) {
      return product.rawMaterialId;
    } else {
      return product.productId;
    }
  };

  const handleToggleCheckbox = (productId) => {
    const updatedProducts = selectedProducts.map((product) => {
      const targetId = getProductId(product);
      if (targetId === productId) {
        return { ...product, checked: !product.checked };
      }
      return product;
    });
    setSelectedProducts(updatedProducts);
  };

  const handleTaxIncludedChange = (productId, checked) => {
    setTaxIncludedMap((prev) => ({
      ...prev,
      [productId]: checked,
    }));

    // Also update the selectedProducts array to keep in sync
    const updatedProducts = selectedProducts.map((product) => {
      const targetId = getProductId(product);
      if (targetId === productId) {
        return { ...product, taxIncluded: checked };
      }
      return product;
    });
    setSelectedProducts(updatedProducts);
  };

  const handleCancel = () => {
    navigate(`/${bussinessUuid}/dashboard/bills/sale`);
  };

  const handleSelect = () => {
    const selected = selectedProducts
      .filter((product) => {
        const catKey = isRawMaterials
          ? "rawMaterialCategory"
          : "productCategory";
        const subKey = isRawMaterials
          ? "rawMaterialSubCategory"
          : "productSubCategory";
        return (
          product.checked &&
          (!selectedCategory || product[catKey] === selectedCategory) &&
          (!selectedSubcategory || product[subKey] === selectedSubcategory)
        );
      })
      .map((product) => ({
        ...product,
        // Ensure taxIncluded is included from the map
        taxIncluded: taxIncludedMap[getProductId(product)] || false,
      }));

    console.log("Selected products with tax info:", selected);
    handleSelectedProducts(selected);
    handleIsOpen();
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setSelectedSubcategory(""); // Reset subcategory when category changes
  };

  const handleSubcategoryChange = (event) => {
    setSelectedSubcategory(event.target.value);
  };

  const getUniqueValues = (key) => {
    const categoryKey = isRawMaterials
      ? "rawMaterialCategory"
      : "productCategory";
    const subKey = isRawMaterials
      ? "rawMaterialSubCategory"
      : "productSubCategory";
    return key === "category"
      ? [...new Set(products.map((product) => product[categoryKey]))]
      : [...new Set(products.map((product) => product[subKey]))];
  };

  const startItemIndex = pageNumber * pageLimit + 1;
  const endItemIndex = Math.min((pageNumber + 1) * pageLimit, products.length);

  // Get filtered products for display
  const getDisplayProducts = () => {
    let filtered =
      selectedCategory || selectedSubcategory ? selectedProducts : pageData();

    return filtered.filter(
      (product) =>
        (!selectedCategory || product.productCategory === selectedCategory) &&
        (!selectedSubcategory ||
          product.productSubCategory === selectedSubcategory)
    );
  };

  const displayProducts = getDisplayProducts();

  return (
    <>
      <div className='mb-2 flex items-center text-gray-900 dark:text-gray-100 text-sm'>
        <label htmlFor='categoryFilter' className='mr-2'>
          {t("MENU.FILTER_CATEGORY")}:
        </label>
        <select
          id='categoryFilter'
          value={selectedCategory}
          onChange={handleCategoryChange}
          className='p-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400'
        >
          <option value=''>{t("MENU.ALL_CATEGORY")}</option>
          {getUniqueValues("category").map((category, index) => (
            <option key={index} value={category}>
              {category}
            </option>
          ))}
        </select>

        <label htmlFor='subcategoryFilter' className='ml-4 mr-2'>
          {t("MENU.FILTER_SUB_CATEGORY")}:
        </label>
        <select
          id='subcategoryFilter'
          value={selectedSubcategory}
          onChange={handleSubcategoryChange}
          className='p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400'
        >
          <option value=''>{t("MENU.ALL_SUB_CATEGORY")}</option>
          {getUniqueValues("subcategory")
            .filter(
              (subcategory) =>
                selectedCategory &&
                products.some((product) => {
                  const catKey = isRawMaterials
                    ? "rawMaterialCategory"
                    : "productCategory";
                  const subKey = isRawMaterials
                    ? "rawMaterialSubCategory"
                    : "productSubCategory";
                  return (
                    product[catKey] === selectedCategory &&
                    product[subKey] === subcategory
                  );
                })
            )
            .map((subcategory, index) => (
              <option key={index} value={subcategory}>
                {subcategory}
              </option>
            ))}
        </select>
      </div>

      {/* Products Table */}
      <div className='max-h-[72vh]'>
        <table className='w-full table-fixed border-collapse'>
          <thead>
            <tr className='bg-gray-100 dark:bg-[#020617] text-gray-800 dark:text-white text-sm'>
              <th className='w-[8%] border dark:bg-[#020617] dark:border-[#020617] dark:text-white p-2'>
                {t("MENU.SELECTED")}
              </th>
              <th className='w-[15%] border dark:bg-[#020617] dark:border-[#020617] dark:text-white p-2'>
                {isServices
                  ? t("REPORT.SERVICE_NAME")
                  : isRawMaterials
                  ? t("FORM.RAW_MATERIAL_NAME")
                  : t("REPORT.PRODUCT_NAME")}
              </th>
              <th className='w-[10%] border dark:bg-[#020617] dark:border-[#020617] dark:text-white p-2'>
                {t("MENU.BATCH")}
              </th>
              <th className='w-[12%] border dark:border-[#020617] p-2 dark:bg-[#020617] dark:text-white'>
                {t("MENU.CATEGORY")}
              </th>
              <th className='w-[12%] border dark:border-[#020617] p-2 dark:bg-[#020617] dark:text-white'>
                {t("MENU.SUB_CATEGORY")}
              </th>
              <th className='w-[10%] border dark:border-[#020617] p-2 dark:bg-[#020617] dark:text-white'>
                {t("MENU.SELL_PRICE")}
              </th>
              <th className='w-[8%] border dark:border-[#020617] p-2 dark:bg-[#020617] dark:text-white'>
                {t("Tax added")}
              </th>
              <th className='w-[15%] border dark:border-[#020617] p-2 dark:bg-[#020617] dark:text-white'>
                {t("Subscription")}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayProducts.map((product, index) => {
              const productId = getProductId(product);
              const isSelected = product.checked;

              return (
                <tr
                  key={index}
                  className={`border dark:border-[#170f2a] text-gray-800 dark:text-white ${
                    isSelected ? "bg-blue-50 dark:bg-blue-900" : ""
                  }`}
                >
                  {/* Selection Checkbox */}
                  <td className='p-2 text-center text-xs'>
                    <input
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => handleToggleCheckbox(productId)}
                      className='w-4 h-4 accent-purple-500 dark:accent-purple-400'
                    />
                  </td>

                  {/* Product/Service Name */}
                  <td className='p-2 text-xs'>
                    {isServices
                      ? product.serviceName
                      : isRawMaterials
                      ? product.rawMaterialName
                      : product.productName}
                  </td>

                  {/* Batch Number */}
                  <td className='p-2 text-xs'>{product.batchNumber || "-"}</td>

                  {/* Category */}
                  <td className='p-2 text-xs'>
                    {isRawMaterials
                      ? product.rawMaterialCategory
                      : product.productCategory}
                  </td>

                  {/* Sub Category */}
                  <td className='p-2 text-xs'>
                    {isRawMaterials
                      ? product.rawMaterialSubCategory
                      : product.productSubCategory}
                  </td>

                  {/* Sale Price */}
                  <td className='p-2 text-xs'>{product.salePrice}</td>

                  {/* Tax Included Checkbox */}
                  <td className='p-2 text-center text-xs'>
                    <input
                      type='checkbox'
                      checked={taxIncludedMap[productId] || false}
                      onChange={(e) =>
                        handleTaxIncludedChange(productId, e.target.checked)
                      }
                      disabled={!isSelected}
                      className={`w-4 h-4 accent-green-500 dark:accent-green-400 ${
                        !isSelected
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:accent-green-600"
                      } ${
                        taxIncludedMap[productId]
                          ? "ring-2 ring-green-200 dark:ring-green-800"
                          : ""
                      }`}
                    />
                    {!isSelected && (
                      <div className='text-[10px] text-gray-400 mt-1'>
                        Included
                      </div>
                    )}
                    {/* Optional: Add a label when checked */}
                    {isSelected && taxIncludedMap[productId] && (
                      <div className='text-[10px] text-green-600 dark:text-green-400 mt-1 font-medium'>
                        Tax Included
                      </div>
                    )}
                  </td>

                  {/* Subscription */}
                  <td className='p-2 text-xs max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap'>
                    {product.subscription?.length
                      ? product.subscription.map((sub, subIndex) => (
                          <div key={subIndex}>
                            {sub.frequency?.trim()} - {sub.subscriptionDisc}
                            {sub.disUnit}
                          </div>
                        ))
                      : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Empty State */}
        {displayProducts.length === 0 && (
          <div className='text-center py-8 text-gray-500 dark:text-gray-400'>
            {t("FORM.NO_PRODUCTS_FOUND")}
          </div>
        )}

        {/* Pagination and Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
          <div className='flex items-center text-sm text-gray-600 dark:text-gray-400'>
            <img
              src={preIcon}
              className='cursor-pointer mr-2'
              alt='previous'
              onClick={previousPage}
              disabled={pageNumber === 0}
            />
            <span className='mx-2'>
              {products.length > 0
                ? `Showing ${startItemIndex} to ${endItemIndex} of ${products.length} entries`
                : "No data available"}
            </span>
            <img
              src={nextIcon}
              className='cursor-pointer ml-2'
              alt='next'
              onClick={nextPage}
              disabled={pageNumber === pageCount - 1}
            />
          </div>

          <div className="w-full sm:w-1/2 flex justify-end">
            {/* <button
              className='px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
              onClick={isPurchase === true ? handleIsOpen : handleCancel}
            >
              {t("MENU.CANCEL")}
            </button> */}
            <button
              className={`w-full py-2 rounded-lg font-semibold transition-all ${
                selectedProducts.some((product) => product.checked)
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              }`}
              onClick={handleSelect}
              disabled={!selectedProducts.some((product) => product.checked)}
            >
              {t("MENU.SELECT")}
            </button>
          </div>
        </div>

        {/* Selected Products Summary */}
        {selectedProducts.some((product) => product.checked) && (
          <div className='mt-3 p-2 bg-green-50 dark:bg-green-900 rounded border border-green-200 dark:border-green-700'>
            <div className='text-sm text-green-800 dark:text-green-200'>
              <strong>Selected:</strong>{" "}
              {selectedProducts.filter((p) => p.checked).length} items |
              <strong> Tax Included:</strong>{" "}
              {
                selectedProducts.filter((p) => p.checked && p.taxIncluded)
                  .length
              }{" "}
              items
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductSelection;
